/**
 * Column contract shared by every Supplier Performance drill-down.
 *
 * One column definition drives both the on-screen table (DrilldownTable) and
 * the Excel export (exportDrilldown), so "the export has the same columns as
 * the table" holds by construction rather than by keeping two lists in step.
 */
import { ReactNode } from 'react';

export interface DrilldownFormatters {
  formatDate: (d: string | Date) => string;
  formatDateTime: (d: string | Date) => string;
  /** Full-precision money: symbol, thousands separators, 2 dp ("£1,234.56"). */
  money: (value: number, currency?: string) => string;
  /** Fallback currency for a money column whose row carries none. */
  currency: string;
  /**
   * A quantity. CR-7262 owns this format platform-wide — up to 3 dp, trailing
   * zeros trimmed, the tenant's own decimal separator and thousands grouping
   * (`PreferencesContext.formatQuantity`). It is INJECTED rather than imported
   * because the rule is a preference, and it is REQUIRED rather than optional
   * because an omitted one would silently restore the defect: this module used
   * to hard-code `toLocaleString('en-GB', { maximumFractionDigits: 2 })`, which
   * truncated a 3 dp quantity AND printed the wrong separator for a
   * comma-decimal tenant.
   */
  qty: (value: CellValue) => string;
}

export type CellValue = string | number | null | undefined;

/** Loosely-typed row for the modal, which receives `unknown[]`. */
export type AnyRow = Record<string, unknown>;

export interface DrilldownColumn<T> {
  key: string;
  label: string;
  /** Header tooltip: what the column means, where its definition is not obvious. */
  title?: string;
  align?: 'left' | 'right' | 'center';
  /** 'qty' → thousands separators, at most 2 dp; 'money' → currency(row). */
  kind?: 'text' | 'qty' | 'money';
  /** The cell's value. Numbers stay numbers so Excel can sum them. */
  value: (row: T) => CellValue;
  /** Currency for a money column; falls back to the page currency. */
  currency?: (row: T) => string | null | undefined;
  /** Richer on-screen cell (links, badges). The export always uses value(). */
  render?: (row: T) => ReactNode;
  /** Long free text: clamp the cell and put the full text in its tooltip. */
  truncate?: boolean;
  /** Extra classes for the <td>. */
  className?: string;
}

/** Parse a serializer decimal string ("6.260") to a number; null when absent. */
export function num(v: CellValue): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** A formatted date, or null when the row has none. */
export function dateOf(fmt: (d: string | Date) => string, v: string | null | undefined): string | null {
  return v ? fmt(v) : null;
}

/**
 * Yes / No for a boolean cell — and '-' when there is no answer.
 *
 * CR-7270: a frozen month's row has no value for flags that only exist after a
 * verdict. Printing 'No' there ASSERTS something the snapshot never recorded,
 * so the cell stays empty instead. (The OTIF Acknowledged column this was
 * written for is gone — CR-7418 folded it into the OTIF Review status, which
 * answers the same way for the same reason, from `review_status` server-side.
 * The rule still binds every other boolean cell here.) REWORK (Aaron, 18 Sep): the empty mark is '-', the same one every
 * other absent cell uses — see `displayValue` below. A caller passing a real
 * boolean (Overridden always; Completed when the status is known) renders
 * Yes / No exactly as before.
 */
export const yesNo = (v: boolean | null | undefined): string =>
  (v === null || v === undefined ? '-' : v ? 'Yes' : 'No');

/**
 * CR-7270: is this row described AS AT a frozen month's boundary? The server
 * sets `frozen_as_at` on exactly those rows (purchasing.services.frozen_arrears).
 */
export function isFrozenRow(row: unknown): boolean {
  return Boolean((row as { frozen_as_at?: string | null } | null)?.frozen_as_at);
}

/**
 * The text the table shows for a column's value.
 *
 * Absent reads '-', live row or frozen (Aaron, 18 Sep 2026, superseding the
 * 15 Sep split). Telling the two apart was never this cell's job — the
 * cached-data pill above the grid says the rows are month-end data — and the
 * split fired on columns the capture never owned, so a live descriptor with no
 * value (a supplier with no group) read as something December had lost.
 */
export function displayValue<T>(column: DrilldownColumn<T>, row: T, f: DrilldownFormatters): string {
  const v = column.value(row);
  if (v === null || v === undefined || v === '') return '-';
  if (column.kind === 'qty') return f.qty(v);
  if (column.kind === 'money') {
    const n = num(v);
    return n === null ? '-' : f.money(n, column.currency?.(row) || f.currency);
  }
  return String(v);
}

/** The value written to the Excel cell: numbers for qty/money, text otherwise. */
export function exportValue<T>(column: DrilldownColumn<T>, row: T): string | number {
  const v = column.value(row);
  if (v === null || v === undefined) return '';
  if (column.kind === 'qty' || column.kind === 'money') {
    const n = num(v);
    return n === null ? '' : n;
  }
  return v;
}
